import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AvailabilityCellVisual } from '../../availability/components/availability-cell-visual';
import { InspectPopoverCell } from '../../availability/components/inspect-popover-cell';
import { resolveDominantEntry } from '../../availability/components/inspect-popover-parts';
import { buildAvailabilityEnquiryHref } from '../../availability/lib/availability-enquiry';
import type {
  AvailabilityGridRow,
  AvailabilityPeriod,
  CellState,
  EntriesGridData,
} from '../../availability/lib/availability-types';
import { useReservationPermissions } from '../../reservations/hooks/use-reservation-permissions';
import {
  useCancelReservation,
  useCloseSale,
  useForceRelease,
  useUpgradeHard,
} from '../../reservations/api/mutations';
import { MEDIA_UNIT_AVAILABILITY_KEY_PREFIX } from '../../reservations/api/queries';
import { formatPolishDate } from '../../reservations/lib/formatters';
import { buildQueueJoinPanelLabels } from '../../reservations/queue-join/build-queue-join-panel-labels';
import { buildQueueJoinSection } from '../../reservations/queue-join/build-queue-join-section';
import { resolveCellQueueWindowContext } from '../../reservations/queue-join/resolve-cell-queue-window-context';
import type { QueueJoinOfferOption } from '../../reservations/queue-join/queue-join-types';
import { useCellQueueJoin } from '../../reservations/queue-join/use-cell-queue-join';
import { useQueueClientOptions } from '../../reservations/queue-join/use-queue-client-options';
import {
  getInventoryDeadlineLabelForPeriod,
  getInventoryOwnerConditionalForPeriod,
  getInventoryOwnerHintForPeriod,
  getInventoryOwnerOfferTitleForPeriod,
  type InventoryAvailabilityPeriod,
  type InventoryOccupancyByRowId,
} from '../inventory/components/inventory-availability-shared';
import { buildInspectQuickActionHandlers } from '../inventory/components/inventory-selection-helpers';

// Own-held cells stay with the grid's multi-select flow; every other occupied state opens the inspect popover.
const INSPECT_STATES: ReadonlySet<CellState> = new Set<CellState>([
  'sold',
  'option-other',
  'reserved-other-soft',
  'reserved-other-hard',
  'unavailable',
  'unknown',
]);

interface UseDetailInspectCellOptions {
  unitId: number;
  city: string | undefined;
  periods: InventoryAvailabilityPeriod[];
  entriesGrid: EntriesGridData;
  occupancyByRowId: InventoryOccupancyByRowId;
  queueOfferOptions?: QueueJoinOfferOption[];
}

function resolveInspectContext(
  openKey: string | null,
  entriesGrid: EntriesGridData,
  periods: InventoryAvailabilityPeriod[],
  unitId: number,
) {
  if (!openKey) return resolveCellQueueWindowContext([], undefined, 0);
  // Row ids are plain numbers, but month period ids ("2026-01") contain a hyphen themselves.
  const separatorIndex = openKey.indexOf('-');
  const rowId = openKey.slice(0, separatorIndex);
  const periodId = openKey.slice(separatorIndex + 1);
  const entries = entriesGrid[rowId]?.[periodId] ?? [];
  return resolveCellQueueWindowContext(
    entries,
    periods.find((p) => p.id === periodId),
    unitId,
  );
}

export function useDetailInspectCell({
  unitId,
  city,
  periods,
  entriesGrid,
  occupancyByRowId,
  queueOfferOptions,
}: UseDetailInspectCellOptions) {
  const { t } = useTranslation('reservations');
  const { t: tInventory } = useTranslation('inventory');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const permissions = useReservationPermissions();
  const upgradeHard = useUpgradeHard();
  const closeSale = useCloseSale();
  const cancelReservation = useCancelReservation();
  const forceRelease = useForceRelease();
  const queueClientOptions = useQueueClientOptions();
  const queueLabels = React.useMemo(
    () =>
      buildQueueJoinPanelLabels(t as (key: string, options?: Record<string, unknown>) => string),
    [t],
  );
  const [openKey, setOpenKey] = React.useState<string | null>(null);

  const queue = useCellQueueJoin(
    React.useMemo(
      () => resolveInspectContext(openKey, entriesGrid, periods, unitId),
      [openKey, entriesGrid, periods, unitId],
    ),
  );

  return React.useCallback(
    (
      row: AvailabilityGridRow,
      period: AvailabilityPeriod,
      cellState: CellState,
      colWidth: number,
    ) => {
      if (cellState === 'free') return undefined;
      const inspectPeriod = periods.find((p) => p.id === period.id);
      const hints = inspectPeriod
        ? {
            ownerHint: getInventoryOwnerHintForPeriod(row.id, inspectPeriod, occupancyByRowId),
            offerTitle: getInventoryOwnerOfferTitleForPeriod(
              row.id,
              inspectPeriod,
              occupancyByRowId,
            ),
            ownerConditional: getInventoryOwnerConditionalForPeriod(
              row.id,
              inspectPeriod,
              occupancyByRowId,
            ),
            deadlineLabel: getInventoryDeadlineLabelForPeriod(
              row.id,
              inspectPeriod,
              occupancyByRowId,
              tInventory('availability.inspect.expiresLabel'),
            ),
          }
        : { ownerConditional: false };
      const visual = (
        <AvailabilityCellVisual
          state={cellState}
          {...hints}
          colWidth={colWidth}
          soldAriaLabel={tCommon('status.sold')}
          optionAriaLabel={tCommon('status.option')}
          reservedAriaLabel={tCommon('status.reserved')}
        />
      );
      if (!INSPECT_STATES.has(cellState)) return visual;

      const key = `${row.id}-${period.id}`;
      const isOpen = openKey === key;
      const entries = entriesGrid[row.id]?.[period.id] ?? [];
      const { dominant } = resolveDominantEntry(entries);
      const dateRangeLabel = inspectPeriod
        ? `${formatPolishDate(inspectPeriod.start)} - ${formatPolishDate(inspectPeriod.end)}`
        : '';

      return (
        <InspectPopoverCell
          trigger={visual}
          isOpen={isOpen}
          onOpenChange={(open) => setOpenKey(open ? key : null)}
          triggerTestId={`detail-inspect-trigger-${row.id}-${period.id}`}
          entries={entries}
          cellState={cellState}
          unitLabel={row.label}
          dateRangeLabel={dateRangeLabel}
          mediaUnitId={Number(row.id)}
          inspectedCellDateFrom={inspectPeriod?.start}
          inspectedCellDateTo={inspectPeriod?.end}
          hasLabelAuthorization={true}
          canUpgradeHard={permissions.canUpgradeHard}
          canCloseSale={permissions.canCloseSale}
          canCancel={permissions.canCancel}
          canForceRelease={permissions.canForceRelease}
          onExpired={() =>
            void queryClient.invalidateQueries({ queryKey: MEDIA_UNIT_AVAILABILITY_KEY_PREFIX })
          }
          {...buildInspectQuickActionHandlers(dominant, unitId, {
            upgradeHard,
            closeSale,
            cancelReservation,
            forceRelease,
          })}
          queueInfo={isOpen ? queue.queueInfo : undefined}
          queueSection={buildQueueJoinSection(isOpen, `detail-inspect-${row.id}-${period.id}`, {
            activeQueueState: queue.activeQueueState,
            currentUserId: queue.currentUserId,
            queueListId: unitId,
            clientOptions: queueClientOptions,
            offerOptions: queueOfferOptions,
            labels: queueLabels,
            onJoin: queue.handleJoin,
            onQueueAction: queue.handleQueueAction,
            isJoinPending: queue.isJoinPending,
          })}
          canManage={false}
          enquireLabel={tInventory('availability.enquireAvailability')}
          enquireHref={buildAvailabilityEnquiryHref(row.label, city, tInventory)}
          setStatusManuallyLabel=""
          testId={`detail-inspect-${row.id}-${period.id}`}
        />
      );
    },
    [
      periods,
      occupancyByRowId,
      entriesGrid,
      openKey,
      unitId,
      city,
      tInventory,
      tCommon,
      permissions,
      upgradeHard,
      closeSale,
      cancelReservation,
      forceRelease,
      queryClient,
      queue,
      queueClientOptions,
      queueOfferOptions,
      queueLabels,
    ],
  );
}
